import createFetchival from '@exodus/fetch/create-fetchival'
import { getName as getCountryName } from 'country-list'
import {
  ASSET_TYPES,
  FILTER_LIST_MODES,
  ORDER_TYPES,
  PAYMENT_METHOD_TYPES,
  PROVIDERS,
} from '../constants.js'
import { ERROR_PAYMENT_METHOD_NOT_SUPPORTED } from '../errors.js'
import { filterByCountries, omitUndefinedValues } from '../utils.js'
import {
  ENABLED_ASSETS,
  ONRAMPER_API_URL,
  ONRAMPER_PROVIDERS,
  PAYMENT_METHODS,
} from './constants.js'
import onramperAdapter from './adapter.js'

export * from './constants.js'
export * from './utils.js'
export { default as adapter } from './adapter.js'

const onramperProvider = (
  {
    apiKey,
    assets: assets_ = ENABLED_ASSETS,
    countries = {},
    countriesMode = FILTER_LIST_MODES.blacklist,
    enabled = true,
    paymentMethods: paymentMethods_ = Object.fromEntries(PAYMENT_METHODS),
    providers: providers_ = ONRAMPER_PROVIDERS,
    url = ONRAMPER_API_URL,
    fetch: globalFetch = fetch.bind(globalThis), // eslint-disable-line no-undef
    paymentMethodTypes,
  } = Object.create(null)
) => {
  if (!apiKey) {
    throw new Error('`apiKey` is required')
  }

  const availableProviders = new Set()
  const availableProvidersByCryptoAsset = new Map()
  const enabledProviders = Object.values(providers_)
  const fetchival = createFetchival({ fetch: globalFetch })
  const fetch_ = fetchival(url)
  const paymentMethods = new Map(Object.entries(paymentMethods_))
  const precisions = new Map()

  const getAssets = async (
    { countryCode, fromAsset, paymentMethod, toAsset, type = ORDER_TYPES.buy } = Object.create(null)
  ) => {
    const availableAssets = []
    const isBuy = type === ORDER_TYPES.buy
    const fiatAsset = isBuy ? fromAsset : toAsset
    const isBuySupported = true
    const isSellSupported = true
    const maxBuyAmount = Number.MAX_SAFE_INTEGER
    const minBuyAmount = 0
    const providerPaymentMethod = paymentMethod
      ? onramperAdapter.fromExodusToProviderPaymentMethod(paymentMethod)
      : undefined

    const [
      { message: providerCryptoAssets },
      {
        message: { fiat: providerFiatAssets },
      },
    ] = await Promise.all([
      fetch_('supported/crypto', { headers: { authorization: apiKey } }).get(
        omitUndefinedValues({ country: countryCode, source: fiatAsset })
      ),
      fetch_('supported', { headers: { authorization: apiKey } }).get(),
    ])

    for (const providerAsset of providerCryptoAssets) {
      const asset = onramperAdapter.fromProviderToExodusAsset(providerAsset.id, assets_)

      if (!asset) {
        continue
      }

      const assetAvailableProviders_ = providerAsset.fiat[0].onramps.filter((provider) => {
        const hasProviderEnabled = enabledProviders.includes(provider.id)
        const hasPaymentMethod =
          !providerPaymentMethod ||
          provider.paymentMethods.some(
            (paymentMethod) => paymentMethod.id === providerPaymentMethod
          )

        return hasProviderEnabled && hasPaymentMethod
      })

      if (assetAvailableProviders_.length === 0) {
        continue
      }

      const assetAvailableProviders = assetAvailableProviders_.map((provider) => provider.id)

      for (const provider of assetAvailableProviders) {
        availableProviders.add(provider)
      }

      availableProvidersByCryptoAsset.set(asset.name, assetAvailableProviders_)
      precisions.set(asset.name, providerAsset.decimals ?? 8)
      availableAssets.push({
        isBuySupported,
        isSellSupported,
        maxBuyAmount,
        minBuyAmount,
        name: asset.name,
        providers: [...assetAvailableProviders],
        type: ASSET_TYPES.crypto,
      })
    }

    if (availableProviders.size === 0) {
      return []
    }

    for (const providerAsset of providerFiatAssets) {
      const name = providerAsset.code.toUpperCase()

      precisions.set(name, providerAsset.decimals ?? 2)
      availableAssets.push({
        isBuySupported,
        isSellSupported,
        maxBuyAmount,
        minBuyAmount,
        name,
        providers: [...availableProviders],
        type: ASSET_TYPES.fiat,
      })
    }

    return availableAssets
  }

  const getCountries = async ({ countryCode, type = ORDER_TYPES.buy } = Object.create(null)) => {
    const availableCountries = []
    const {
      message: { defaults: providerCountries },
    } = await fetch_('supported/defaults/all', { headers: { authorization: apiKey } }).get(
      omitUndefinedValues({ country: countryCode.toLowerCase(), type })
    )

    for (const country of Object.keys(providerCountries)) {
      const countryCode = country.toUpperCase()
      const isSupported = !filterByCountries(countries, countryCode, {
        mode: countriesMode,
        properties: [ORDER_TYPES.buy, ORDER_TYPES.sell],
      })

      if (isSupported) {
        availableCountries.push({
          code: countryCode,
          isBuySupported: true,
          isSellSupported: true,
          name: getCountryName(countryCode),
          providers: enabledProviders,
        })
      }
    }

    return availableCountries
  }

  const getOrder = async (
    id,
    { serverClient, serverOrder, type = ORDER_TYPES.buy } = Object.create(null)
  ) => {
    const order = await serverClient.getOrders({ provider: PROVIDERS.onramper, orderId: id })

    return onramperAdapter.fromProviderToExodusOrder(
      {
        ...serverOrder,
        ...order,
        id,
        type,
      },
      assets_
    )
  }

  const getQuote = async ({
    countryCode,
    fromAmount,
    fromAsset,
    isRecurringPayment = false,
    paymentMethod = PAYMENT_METHOD_TYPES.card,
    toAmount,
    toAsset,
    type = ORDER_TYPES.buy,
    walletAddress,
  }) => {
    if (!fromAsset) {
      throw new Error('`fromAsset` is required')
    }

    if (!toAsset) {
      throw new Error('`toAsset` is required')
    }

    if (!fromAmount && !toAmount) {
      throw new Error('either `fromAmount` or `toAmount` is required')
    }

    const isBuy = type === ORDER_TYPES.buy
    const input = fromAmount ? 'source' : 'destination'
    const providerFromAsset = isBuy
      ? fromAsset.toLowerCase()
      : onramperAdapter.fromExodusToProviderAsset(fromAsset, assets_)

    const providerToAsset = isBuy
      ? onramperAdapter.fromExodusToProviderAsset(toAsset, assets_)
      : toAsset.toLowerCase()

    const providerPaymentMethod = onramperAdapter.fromExodusToProviderPaymentMethod(paymentMethod, {
      assets: assets_,
      country: countryCode,
      currency: isBuy ? fromAsset : toAsset,
      paymentMethods,
      paymentMethodTypes,
    })

    if (!providerPaymentMethod) {
      throw new Error(ERROR_PAYMENT_METHOD_NOT_SUPPORTED.message)
    }

    const [providerQuotes] = await Promise.all([
      fetch_(
        `quotes/${encodeURIComponent(providerFromAsset)}/${encodeURIComponent(providerToAsset)}`,
        {
          headers: { authorization: apiKey },
        }
      ).get(
        omitUndefinedValues({
          amount: fromAmount || toAmount,
          country: countryCode?.toLowerCase(),
          input,
          isRecurringPayment,
          paymentMethod: providerPaymentMethod,
          type,
          walletAddress,
        })
      ),
      precisions.size === 0 && getAssets({ countryCode, fromAsset, paymentMethod, toAsset, type }),
    ])

    const errors = []
    const quotes = []

    for (const quote of providerQuotes) {
      if (quote.errors && !quote.errors.some(({ type }) => type === 'LimitMismatch')) {
        for (const error of quote.errors) {
          errors.push(error)
        }

        continue
      }

      if (availableProviders.has(quote.ramp)) {
        quotes.push(
          onramperAdapter.fromProviderToExodusQuote({
            ...quote,
            amount: fromAmount || toAmount,
            fromAsset,
            input,
            precisions,
            toAsset,
            type,
          })
        )
      }
    }

    if (quotes.length === 0 && errors.length > 0) {
      throw new Error(errors.pop().message)
    }

    return quotes
  }

  const getPaymentMethod = (
    paymentMethod,
    {
      country,
      cryptoAsset,
      currency,
      orderType = ORDER_TYPES.buy,
      provider,
      ...parameters
    } = Object.create(null)
  ) => {
    const availablePaymentMethods = new Map()

    if (cryptoAsset && provider) {
      const providerPaymentMethods =
        availableProvidersByCryptoAsset
          .get(cryptoAsset)
          ?.find((provider_) => provider_.id === provider)?.paymentMethods ?? []

      for (const paymentMethod of providerPaymentMethods) {
        if (paymentMethods.has(paymentMethod.id)) {
          availablePaymentMethods.set(paymentMethod.id, paymentMethods.get(paymentMethod.id))
        }
      }
    }

    return onramperAdapter.fromExodusToProviderPaymentMethod(paymentMethod, {
      assets: assets_,
      paymentMethods: provider ? availablePaymentMethods : paymentMethods,
      country,
      cryptoAsset,
      currency,
      orderType,
      paymentMethodTypes,
      ...parameters,
    })
  }

  return {
    name: PROVIDERS.onramper,
    enabled,
    getAssets,
    getCountries,
    getOrder,
    getPaymentMethod,
    getQuote,
    orderTypes: [ORDER_TYPES.buy, ORDER_TYPES.sell],
    providers: enabledProviders,
  }
}

export default onramperProvider
